import React, { JSX } from "react";

import * as MuiIcons from "@mui/icons-material";
import { Alert, useTheme } from "@mui/material";
import { type SxProps } from "@mui/system";

export type AlertType = "error" | "warning" | "info";

interface CustomAlertProps {
  type?: AlertType;
  children: React.ReactNode;
  iconName?: string;
}

export function CustomAlert({
  type,
  children,
  iconName,
}: CustomAlertProps): JSX.Element | null {
  const theme = useTheme();

  const commonAlertStyles: SxProps = {
    alignItems: "center",
    borderRadius: 1,
    my: 2,
    "& .MuiAlert-message": {
      width: "100%",
    },
  };

  if (!type) {
    return <>{children}</>;
  }

  const getIcon = (): React.ReactNode => {
    if (iconName) {
      const IconComponent = (MuiIcons as Record<string, React.ComponentType>)[
        iconName
      ];
      if (IconComponent) {
        return <IconComponent />;
      }
      // [KS] Fallback to default if icon name is not found
      console.warn(`Icon "${iconName}" not found in MUI icons library`);
    }

    switch (type) {
      case "error":
        return <MuiIcons.Error />;
      case "warning":
        return <MuiIcons.Warning />;
      case "info":
        return <MuiIcons.Info />;
      default:
        return <MuiIcons.Info />;
    }
  };

  switch (type) {
    case "error":
      return (
        <Alert severity="error" icon={getIcon()} sx={commonAlertStyles}>
          {children}
        </Alert>
      );
    case "warning":
      return (
        <Alert severity="warning" icon={getIcon()} sx={commonAlertStyles}>
          {children}
        </Alert>
      );
    case "info":
      return (
        <Alert
          severity="info"
          icon={getIcon()}
          sx={{
            ...commonAlertStyles,
            backgroundColor: theme.palette.info.contrastText,
          }}
        >
          {children}
        </Alert>
      );
    default:
      return <>{children}</>;
  }
}
